import type { ContactWebGraphNode } from './types';

export type NodePosition = { readonly x: number; readonly y: number };

const HOUSEHOLD_RING_RADIUS = 320;
const PROFESSIONAL_RING_RADIUS = 700;
const MIN_PROFESSIONAL_SPACING = 220;

function ringPositions(sortedIds: readonly string[], radius: number): Map<string, NodePosition> {
  const positions = new Map<string, NodePosition>();
  const count = sortedIds.length;
  sortedIds.forEach((id, index) => {
    const angle = (index / Math.max(count, 1)) * 2 * Math.PI - Math.PI / 2;
    positions.set(id, { x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
  });
  return positions;
}

function sortedIdsByName(nodes: readonly ContactWebGraphNode[]): string[] {
  return [...nodes].sort((a, b) => a.name.localeCompare(b.name)).map((node) => node.id);
}

/**
 * Deterministic two-ring layout: households on an inner ring, professionals on an
 * outer ring, ordered alphabetically. Positions never depend on which relationship
 * types are toggled on, so cards stay put when the user flips a filter.
 */
export function layoutContactWebNodes(nodes: readonly ContactWebGraphNode[]): Map<string, NodePosition> {
  const households = nodes.filter((node) => node.kind === 'household');
  const professionals = nodes.filter((node) => node.kind === 'professional');

  const professionalRadius = Math.max(
    PROFESSIONAL_RING_RADIUS,
    (professionals.length * MIN_PROFESSIONAL_SPACING) / (2 * Math.PI),
  );

  return new Map([
    ...ringPositions(sortedIdsByName(households), HOUSEHOLD_RING_RADIUS),
    ...ringPositions(sortedIdsByName(professionals), professionalRadius),
  ]);
}
